import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Building,
  Calendar,
  ChevronDown,
  ChevronRight,
  ChevronUp,
  FileText,
  Filter,
  HelpCircle,
  Search,
  Stethoscope,
  Trash2,
  User,
  X,
} from 'lucide-react';
import {
  deleteHospitalReport,
  getCohorts,
  getDepartmentTermCapacities,
  getHospitalReportList,
  getInstitutions,
  getUserInfo,
} from '../services/api';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import LoadingSpinner from './LoadingSpinner';
import OnboardingTour from './OnboardingTour';
import '../styles/HospitalReportListPage.css';

const TOUR_STEPS = [
  {
    target: '[data-tour="report-list-header"]',
    title: '申し送り一覧ページ',
    content: 'このページでは、病院・科ごとの申し送り情報を確認できます。',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-tour="report-list-create-button"]',
    title: '申し送りを作成する',
    content: '新しい申し送りを登録するときは、このボタンからフォームを開きます。',
    placement: 'bottom',
  },
  {
    target: '[data-tour="report-list-filter"]',
    title: 'フィルター検索',
    content: '病院、科、ターム、評価条件などで一覧を絞り込めます。キーワード検索も使えます。',
    placement: 'bottom',
  },
  {
    target: '[data-tour="report-list-results"]',
    title: '検索結果一覧',
    content: '条件に一致する申し送りがここに表示されます。各行から詳細を確認できます。',
    placement: 'top',
  },
  {
    target: '[data-tour="report-list-sort"]',
    title: '並び替え',
    content: '表のヘッダーをクリックすると、病院名や更新日時などで並び替えできます。',
    placement: 'bottom',
  },
];

const TERM_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const ITEMS_PER_PAGE = 20;
const RATING_OPTIONS = [1, 2, 3, 4, 5];

const BUSYNESS_LABELS = {
  1: 'かなり余裕',
  2: 'やや余裕',
  3: '標準',
  4: '忙しい',
  5: 'かなり忙しい',
};

const TOUR_OPTION_LABELS = {
  possible: '見学可',
  impossible: '見学不可',
  unknown: '見学不明',
};

const MEAL_LABELS = {
  none: 'なし',
  free: '無料',
  paid: '有料',
};

const FREE_STUDY_TIME_LABELS = {
  over_three_hours: '3時間以上',
  one_to_three_hours: '1〜3時間',
  thirty_to_sixty_minutes: '30〜60分',
  almost_none: 'ほぼなし',
  none: 'なし',
};

const NOTEBOOK_LM_URL = 'https://notebooklm.google.com/notebook/643f19f3-dacc-4a38-b34a-8de346a93a35?authuser=1';

const getMealLabel = (value) => MEAL_LABELS[value] || '-';

const formatRating = (value) => {
  if (value == null || value === '') return '-';
  return `${value}/5`;
};

const countActiveFilters = (values) => values.filter((value) => value !== '' && value != null).length;

function HospitalReportListPage() {
  const [hospitalList, setHospitalList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [cohortList, setCohortList] = useState([]);

  const [selectedHospital, setSelectedHospital] = useState('');
  const [selectedDepartment, setSelectedDepartment] = useState('');
  const [selectedTerm, setSelectedTerm] = useState('');
  const [selectedCohort, setSelectedCohort] = useState('');
  const [selectedTypicalStartTimeOperator, setSelectedTypicalStartTimeOperator] = useState('gte');
  const [selectedTypicalStartTime, setSelectedTypicalStartTime] = useState('');
  const [selectedTypicalEndTimeOperator, setSelectedTypicalEndTimeOperator] = useState('lte');
  const [selectedTypicalEndTime, setSelectedTypicalEndTime] = useState('');
  const [selectedLateDaysPerWeekOperator, setSelectedLateDaysPerWeekOperator] = useState('gte');
  const [selectedLateDaysPerWeek, setSelectedLateDaysPerWeek] = useState('');
  const [selectedFreeStudyTime, setSelectedFreeStudyTime] = useState('');
  const [selectedHasNightDuty, setSelectedHasNightDuty] = useState('');
  const [selectedBusyness, setSelectedBusyness] = useState('');
  const [selectedHospitalTourOption, setSelectedHospitalTourOption] = useState('');
  const [selectedCarRequirement, setSelectedCarRequirement] = useState('');
  const [selectedAssignmentBurden, setSelectedAssignmentBurden] = useState('');
  const [selectedSelfStudyAvailability, setSelectedSelfStudyAvailability] = useState('');
  const [selectedHandsOnOpportunity, setSelectedHandsOnOpportunity] = useState('');
  const [selectedLodgingConvenience, setSelectedLodgingConvenience] = useState('');
  const [selectedTeachingQuality, setSelectedTeachingQuality] = useState('');
  const [selectedQuestionApproachability, setSelectedQuestionApproachability] = useState('');
  const [selectedDepartmentAtmosphere, setSelectedDepartmentAtmosphere] = useState('');
  const [selectedDoctorPersonality, setSelectedDoctorPersonality] = useState('');
  const [selectedBreakfast, setSelectedBreakfast] = useState('');
  const [selectedLunch, setSelectedLunch] = useState('');
  const [selectedDinner, setSelectedDinner] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  const [reportList, setReportList] = useState([]);
  const [filteredReportList, setFilteredReportList] = useState([]);
  const [displayedReports, setDisplayedReports] = useState([]);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMoreItems, setHasMoreItems] = useState(true);
  const [error, setError] = useState('');

  const [sortKey, setSortKey] = useState('updated_at');
  const [sortOrder, setSortOrder] = useState('desc');

  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [expandedCardId, setExpandedCardId] = useState(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [showTour, setShowTour] = useState(false);

  const [currentUser, setCurrentUser] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [reportToDelete, setReportToDelete] = useState(null);
  const [deleteFormData, setDeleteFormData] = useState({
    hospitalName: '',
    departmentName: '',
    userName: '',
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteStep, setDeleteStep] = useState(1);
  const [deleteToken, setDeleteToken] = useState('');
  const [deleteTokenInput, setDeleteTokenInput] = useState('');

  const observerRef = useRef(null);
  const loadingMoreRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const resultsRef = useRef(null);
  const topRef = useRef(null);

  const isMobileView = windowWidth <= 768;
  const isTabletView = windowWidth <= 1024 && windowWidth > 768;

  const formatDate = useCallback(
    (dateString) => {
      if (!dateString) return '-';

      try {
        const date = new Date(dateString);
        if (Number.isNaN(date.getTime())) {
          return '日付不明';
        }

        const now = new Date();
        const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const yesterdayStart = new Date(todayStart);
        yesterdayStart.setDate(todayStart.getDate() - 1);
        const targetDay = new Date(date.getFullYear(), date.getMonth(), date.getDate());

        const timeFormat = { hour: '2-digit', minute: '2-digit' };

        if (windowWidth <= 480) {
          return date.toLocaleString('ja-JP', {
            month: '2-digit',
            day: '2-digit',
            ...timeFormat,
          });
        }

        if (targetDay.getTime() === todayStart.getTime()) {
          return `今日 ${date.toLocaleString('ja-JP', timeFormat)}`;
        }
        if (targetDay.getTime() === yesterdayStart.getTime()) {
          return `昨日 ${date.toLocaleString('ja-JP', timeFormat)}`;
        }

        return date.toLocaleString('ja-JP', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          ...timeFormat,
        });
      } catch (formatError) {
        console.error('日付の整形に失敗しました:', formatError);
        return '日付不明';
      }
    },
    [windowWidth]
  );

  const getSummaryBadges = useCallback((item) => {
    const badges = [
      item.busyness ? `忙しさ ${BUSYNESS_LABELS[item.busyness] || item.busyness}` : null,
      item.assignment_burden ? `課題 ${formatRating(item.assignment_burden)}` : null,
      item.self_study_availability ? `自習 ${formatRating(item.self_study_availability)}` : null,
      item.hands_on_opportunity ? `手技 ${formatRating(item.hands_on_opportunity)}` : null,
      item.teaching_quality ? `教育 ${formatRating(item.teaching_quality)}` : null,
      item.question_approachability ? `質問 ${formatRating(item.question_approachability)}` : null,
      item.department_atmosphere ? `雰囲気 ${formatRating(item.department_atmosphere)}` : null,
      item.doctor_personality ? `人柄 ${formatRating(item.doctor_personality)}` : null,
      item.lodging_convenience ? `宿舎 ${formatRating(item.lodging_convenience)}` : null,
      item.car_requirement ? `車 ${formatRating(item.car_requirement)}` : null,
      item.hospital_tour_option ? TOUR_OPTION_LABELS[item.hospital_tour_option] || item.hospital_tour_option : null,
    ];

    return badges.filter(Boolean).slice(0, 6);
  }, []);

  const getMealSummary = useCallback((item) => {
    const meals = [
      item.breakfast ? `朝 ${getMealLabel(item.breakfast)}` : null,
      item.lunch ? `昼 ${getMealLabel(item.lunch)}` : null,
      item.dinner ? `夕 ${getMealLabel(item.dinner)}` : null,
    ].filter(Boolean);

    return meals.join(' / ');
  }, []);

  const getBusynessMetaSummary = useCallback((item) => {
    const parts = [
      item.typical_start_time ? `始 ${item.typical_start_time}` : null,
      item.typical_end_time ? `終 ${item.typical_end_time}` : null,
      item.late_days_per_week !== null && item.late_days_per_week !== undefined ? `17時以降 ${item.late_days_per_week}日/週` : null,
      item.free_study_time ? `自由時間 ${FREE_STUDY_TIME_LABELS[item.free_study_time] || item.free_study_time}` : null,
      item.has_night_duty === true ? '当直あり' : null,
      item.has_night_duty === false ? '当直なし' : null,
    ].filter(Boolean);

    return parts.join(' / ');
  }, []);

  const updateDisplayedData = useCallback((page, data) => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    setDisplayedReports(data.slice(startIndex, endIndex));
  }, []);

  const clearAllFilters = useCallback(() => {
    setSelectedHospital('');
    setSelectedDepartment('');
    setSelectedTerm('');
    setSelectedCohort('');
    setSelectedTypicalStartTimeOperator('gte');
    setSelectedTypicalStartTime('');
    setSelectedTypicalEndTimeOperator('lte');
    setSelectedTypicalEndTime('');
    setSelectedLateDaysPerWeekOperator('gte');
    setSelectedLateDaysPerWeek('');
    setSelectedFreeStudyTime('');
    setSelectedHasNightDuty('');
    setSelectedBusyness('');
    setSelectedHospitalTourOption('');
    setSelectedCarRequirement('');
    setSelectedAssignmentBurden('');
    setSelectedSelfStudyAvailability('');
    setSelectedHandsOnOpportunity('');
    setSelectedLodgingConvenience('');
    setSelectedTeachingQuality('');
    setSelectedQuestionApproachability('');
    setSelectedDepartmentAtmosphere('');
    setSelectedDoctorPersonality('');
    setSelectedBreakfast('');
    setSelectedLunch('');
    setSelectedDinner('');
    setSearchKeyword('');
  }, []);

  const handleScroll = useCallback(() => {
    const scrollY = window.scrollY || document.documentElement.scrollTop;
    setShowScrollTop(scrollY > 300);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    if (topRef.current) {
      setTimeout(() => topRef.current.focus(), 300);
    }
  }, []);

  const generateDeleteToken = useCallback(() => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let token = '';
    for (let index = 0; index < 20; index += 1) {
      token += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return token;
  }, []);

  const handleDeleteClick = useCallback(
    (reportItem) => {
      if (!reportItem?.latest_report_id || !reportItem?.hospital_name || !reportItem?.department_name) {
        setError('削除対象のレポート情報を取得できませんでした。');
        return;
      }

      setReportToDelete(reportItem);
      setDeleteFormData({ hospitalName: '', departmentName: '', userName: '' });
      setDeleteError('');
      setDeleteStep(1);
      setDeleteToken(generateDeleteToken());
      setDeleteTokenInput('');
      setIsDeleteModalOpen(true);
    },
    [generateDeleteToken]
  );

  const handleCloseModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setReportToDelete(null);
    setDeleteFormData({ hospitalName: '', departmentName: '', userName: '' });
    setDeleteError('');
    setIsDeleting(false);
    setDeleteStep(1);
    setDeleteToken('');
    setDeleteTokenInput('');
  }, []);

  const handleDeleteFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setDeleteFormData((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (deleteStep === 1) {
      const isHospitalMatch = deleteFormData.hospitalName.trim() === reportToDelete?.hospital_name;
      const isDepartmentMatch = deleteFormData.departmentName.trim() === reportToDelete?.department_name;
      const isUserMatch = deleteFormData.userName.trim() === currentUser?.username;

      if (isHospitalMatch && isDepartmentMatch && isUserMatch) {
        setDeleteStep(2);
        setDeleteError('');
        return;
      }

      let nextError = '入力内容が一致しません。';
      if (!isHospitalMatch) nextError += ' 病院名を確認してください。';
      if (!isDepartmentMatch) nextError += ' 科名を確認してください。';
      if (!isUserMatch) nextError += ' ユーザー名を確認してください。';
      setDeleteError(nextError);
      return;
    }

    if (deleteTokenInput.trim() !== deleteToken) {
      setDeleteError('確認コードが一致しません。表示されているコードを正確に入力してください。');
      return;
    }

    if (!reportToDelete?.latest_report_id || !currentUser?.username) {
      setDeleteError('削除に必要な情報が不足しています。');
      return;
    }

    setIsDeleting(true);
    setDeleteError('');

    try {
      await deleteHospitalReport(reportToDelete.latest_report_id, {
        verification_token: deleteToken,
        hospital_name: deleteFormData.hospitalName.trim(),
        department_name: deleteFormData.departmentName.trim(),
        user_name: deleteFormData.userName.trim(),
      });

      setReportList((prev) => prev.filter((item) => item.latest_report_id !== reportToDelete.latest_report_id));
      setFilteredReportList((prev) => prev.filter((item) => item.latest_report_id !== reportToDelete.latest_report_id));
      handleCloseModal();
    } catch (deleteApiError) {
      const message = deleteApiError?.message || 'レポートの削除に失敗しました。時間をおいて再試行してください。';
      setDeleteError(message);
      setIsDeleting(false);
    }
  }, [
    currentUser,
    deleteFormData.departmentName,
    deleteFormData.hospitalName,
    deleteFormData.userName,
    deleteStep,
    deleteToken,
    deleteTokenInput,
    handleCloseModal,
    reportToDelete,
  ]);

  const loadMoreItems = useCallback(() => {
    if (!isMobileView || !hasMoreItems || isLoadingMore) return;

    setIsLoadingMore(true);
    const currentDisplayedCount = displayedReports.length;
    const nextItems = filteredReportList.slice(currentDisplayedCount, currentDisplayedCount + ITEMS_PER_PAGE);

    setTimeout(() => {
      setDisplayedReports((prev) => [...prev, ...nextItems]);
      setHasMoreItems(currentDisplayedCount + nextItems.length < filteredReportList.length);
      setIsLoadingMore(false);
    }, 300);
  }, [displayedReports.length, filteredReportList, hasMoreItems, isLoadingMore, isMobileView]);

  const renderListSummary = useCallback(() => {
    if (isLoading || error) return null;

    const filters = [];
    if (selectedHospital) {
      const hospital = hospitalList.find((item) => item.id === parseInt(selectedHospital, 10));
      if (hospital) filters.push(`病院「${hospital.name}」`);
    }
    if (selectedDepartment) {
      const department = departmentList.find((item) => item.id === parseInt(selectedDepartment, 10));
      if (department) filters.push(`科「${department.name}」`);
    }
    if (selectedTerm) filters.push(`ターム「${selectedTerm}」`);
    if (selectedCohort) {
      const cohort = cohortList.find((item) => item.id === parseInt(selectedCohort, 10));
      if (cohort) filters.push(`コホート「${cohort.name}」`);
    }
    if (selectedTypicalStartTime) filters.push(`始業「${selectedTypicalStartTimeOperator === 'lte' ? '以前' : '以降'} ${selectedTypicalStartTime}」`);
    if (selectedTypicalEndTime) filters.push(`終業「${selectedTypicalEndTimeOperator === 'lte' ? '以前' : '以降'} ${selectedTypicalEndTime}」`);
    if (selectedLateDaysPerWeek !== '') filters.push(`17時以降「${selectedLateDaysPerWeekOperator === 'lte' ? '以下' : '以上'} 週${selectedLateDaysPerWeek}日」`);
    if (selectedFreeStudyTime) filters.push(`自由時間「${FREE_STUDY_TIME_LABELS[selectedFreeStudyTime] || selectedFreeStudyTime}」`);
    if (selectedHasNightDuty !== '') filters.push(`当直「${selectedHasNightDuty === 'true' ? 'あり' : 'なし'}」`);
    if (selectedBusyness) filters.push(`忙しさ「${BUSYNESS_LABELS[selectedBusyness] || selectedBusyness}」`);
    if (selectedHospitalTourOption) filters.push(`病院見学「${TOUR_OPTION_LABELS[selectedHospitalTourOption] || selectedHospitalTourOption}」`);
    if (selectedCarRequirement) filters.push(`車の必要性「${formatRating(selectedCarRequirement)}」`);
    if (selectedAssignmentBurden) filters.push(`課題負担「${formatRating(selectedAssignmentBurden)}」`);
    if (selectedSelfStudyAvailability) filters.push(`自習時間「${formatRating(selectedSelfStudyAvailability)}」`);
    if (selectedHandsOnOpportunity) filters.push(`手技「${formatRating(selectedHandsOnOpportunity)}」`);
    if (selectedLodgingConvenience) filters.push(`宿舎「${formatRating(selectedLodgingConvenience)}」`);
    if (selectedTeachingQuality) filters.push(`教育の質「${formatRating(selectedTeachingQuality)}」`);
    if (selectedQuestionApproachability) filters.push(`質問のしやすさ「${formatRating(selectedQuestionApproachability)}」`);
    if (selectedDepartmentAtmosphere) filters.push(`医局の雰囲気「${formatRating(selectedDepartmentAtmosphere)}」`);
    if (selectedDoctorPersonality) filters.push(`医師の人柄「${formatRating(selectedDoctorPersonality)}」`);
    if (selectedBreakfast) filters.push(`朝食「${getMealLabel(selectedBreakfast)}」`);
    if (selectedLunch) filters.push(`昼食「${getMealLabel(selectedLunch)}」`);
    if (selectedDinner) filters.push(`夕食「${getMealLabel(selectedDinner)}」`);
    if (searchKeyword.trim()) filters.push(`キーワード「${searchKeyword.trim()}」`);

    const resultCount = filteredReportList.length;
    const totalCount = reportList.length;
    const summary =
      filters.length > 0
        ? `${filters.join('、')} の検索結果: ${resultCount}件`
        : totalCount > 0
          ? `全ての申し送り: ${totalCount}件`
          : '申し送りデータはありません。';

    return (
      <div className="fg-result-summary">
        <div className="fg-result-summary-main">
          <span aria-live="polite">{summary}</span>
          {filters.length > 0 && (
            <button type="button" className="fg-clear-filters-button" onClick={clearAllFilters}>
              条件をクリア
            </button>
          )}
        </div>
        {filters.length > 0 && (
          <div className="fg-active-filters">
            {filters.map((filter) => (
              <span key={filter} className="fg-active-filter-chip">
                {filter}
              </span>
            ))}
          </div>
        )}
        {!isMobileView && totalPages > 1 && resultCount > 0 && (
          <span className="fg-page-info">
            {`${(currentPage - 1) * ITEMS_PER_PAGE + 1} - ${Math.min(currentPage * ITEMS_PER_PAGE, resultCount)} 件目を表示`}
            <span style={{ marginLeft: '10px' }}>({currentPage} / {totalPages} ページ)</span>
          </span>
        )}
      </div>
    );
  }, [
    clearAllFilters,
    cohortList,
    currentPage,
    departmentList,
    error,
    filteredReportList.length,
    hospitalList,
    isLoading,
    isMobileView,
    reportList.length,
    searchKeyword,
    selectedAssignmentBurden,
    selectedBreakfast,
    selectedBusyness,
    selectedCarRequirement,
    selectedCohort,
    selectedDepartment,
    selectedDepartmentAtmosphere,
    selectedDinner,
    selectedDoctorPersonality,
    selectedFreeStudyTime,
    selectedHandsOnOpportunity,
    selectedHospital,
    selectedHospitalTourOption,
    selectedHasNightDuty,
    selectedLateDaysPerWeekOperator,
    selectedLunch,
    selectedLateDaysPerWeek,
    selectedLodgingConvenience,
    selectedQuestionApproachability,
    selectedSelfStudyAvailability,
    selectedTeachingQuality,
    selectedTerm,
    selectedTypicalEndTimeOperator,
    selectedTypicalEndTime,
    selectedTypicalStartTimeOperator,
    selectedTypicalStartTime,
    totalPages,
  ]);

  const handlePageChange = useCallback(
    (page) => {
      if (page < 1 || page > totalPages) return;
      setCurrentPage(page);
      if (resultsRef.current) {
        resultsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    },
    [totalPages]
  );

  const handleSort = useCallback((column) => {
    setCurrentPage(1);
    setSortKey((prev) => {
      if (prev === column) {
        setSortOrder((current) => (current === 'asc' ? 'desc' : 'asc'));
        return prev;
      }
      setSortOrder('asc');
      return column;
    });
  }, []);

  const renderSortIcon = useCallback(
    (column) => {
      if (sortKey === column) {
        return sortOrder === 'asc' ? <ArrowUp size={14} /> : <ArrowDown size={14} />;
      }
      return <ArrowUpDown size={14} className="opacity-30" />;
    },
    [sortKey, sortOrder]
  );

  const renderTableHeaders = useCallback(() => (
    <tr data-tour="report-list-sort">
      <th
        onClick={() => handleSort('hospital_name')}
        className={`fg-sortable ${sortKey === 'hospital_name' ? 'active-sort' : ''}`}
        aria-sort={sortKey === 'hospital_name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <div className="th-content">
          <Building size={16} className="mr-1" />
          病院名
          <span className="sort-icon" title="クリックして並び替え">
            {renderSortIcon('hospital_name')}
          </span>
        </div>
      </th>
      <th
        onClick={() => handleSort('department_name')}
        className={`fg-sortable ${sortKey === 'department_name' ? 'active-sort' : ''}`}
        aria-sort={sortKey === 'department_name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <div className="th-content">
          <Stethoscope size={16} className="mr-1" />
          科名
          <span className="sort-icon" title="クリックして並び替え">
            {renderSortIcon('department_name')}
          </span>
        </div>
      </th>
      {!isMobileView && (
        <th
          onClick={() => handleSort('term')}
          className={`fg-sortable ${sortKey === 'term' ? 'active-sort' : ''}`}
          aria-sort={sortKey === 'term' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
          <div className="th-content">
            <Calendar size={16} className="mr-1" />
            ターム
            <span className="sort-icon" title="クリックして並び替え">
              {renderSortIcon('term')}
            </span>
          </div>
        </th>
      )}
      {!isMobileView && !isTabletView && (
        <th
          onClick={() => handleSort('last_updated_user')}
          className={`fg-sortable ${sortKey === 'last_updated_user' ? 'active-sort' : ''}`}
          aria-sort={sortKey === 'last_updated_user' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
          <div className="th-content">
            <User size={16} className="mr-1" />
            更新ユーザー
            <span className="sort-icon" title="クリックして並び替え">
              {renderSortIcon('last_updated_user')}
            </span>
          </div>
        </th>
      )}
      {!isMobileView && !isTabletView && (
        <th
          onClick={() => handleSort('cohort_name')}
          className={`fg-sortable ${sortKey === 'cohort_name' ? 'active-sort' : ''}`}
          aria-sort={sortKey === 'cohort_name' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
        >
          <div className="th-content">
            <User size={16} className="mr-1" />
            コホート
            <span className="sort-icon" title="クリックして並び替え">
              {renderSortIcon('cohort_name')}
            </span>
          </div>
        </th>
      )}
      {!isMobileView && <th>評価サマリー</th>}
      <th
        onClick={() => handleSort('updated_at')}
        className={`fg-sortable ${sortKey === 'updated_at' ? 'active-sort' : ''}`}
        aria-sort={sortKey === 'updated_at' ? (sortOrder === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <div className="th-content">
          <Calendar size={16} className="mr-1" />
          更新日時
          <span className="sort-icon" title="クリックして並び替え">
            {renderSortIcon('updated_at')}
          </span>
        </div>
      </th>
      <th>詳細</th>
      <th>削除</th>
    </tr>
  ), [handleSort, isMobileView, isTabletView, renderSortIcon, sortKey, sortOrder]);

  const renderTableRows = useCallback(
    () =>
      displayedReports.map((item, index) => {
        const summaryBadges = getSummaryBadges(item);
        const mealSummary = getMealSummary(item);
        const busynessMetaSummary = getBusynessMetaSummary(item);

        return (
          <tr key={item.latest_report_id || `${item.hospital_name}-${item.department_name}-${index}`} className={index % 2 === 0 ? 'fg-row-even' : 'fg-row-odd'}>
            <td>{item.hospital_name || '-'}</td>
            <td>{item.department_name || '-'}</td>
            {!isMobileView && <td>{item.term ?? '-'}</td>}
            {!isMobileView && !isTabletView && <td>{item.last_updated_user || '-'}</td>}
            {!isMobileView && !isTabletView && <td>{item.cohort_name || '-'}</td>}
            {!isMobileView && (
              <td className="fg-summary-cell">
                <div className="fg-summary-badges">
                  {summaryBadges.length > 0 ? (
                    summaryBadges.map((badge) => (
                      <span key={badge} className="fg-summary-badge">
                        {badge}
                      </span>
                    ))
                  ) : (
                    <span className="fg-summary-empty">評価未設定</span>
                  )}
                </div>
                {busynessMetaSummary && <div className="fg-summary-subtext">{busynessMetaSummary}</div>}
                {mealSummary && <div className="fg-summary-subtext">{mealSummary}</div>}
              </td>
            )}
            <td>{formatDate(item.last_updated_at)}</td>
            <td className="fg-action-cell">
              {item.latest_report_id ? (
                <Link to={`/hospital-reports/${item.latest_report_id}`} className="fg-link">
                  詳細
                  <ChevronRight size={14} className="ml-1" />
                </Link>
              ) : (
                '-'
              )}
            </td>
            <td className="fg-action-cell fg-delete-cell">
              {item.latest_report_id ? (
                <button
                  type="button"
                  className="fg-delete-button"
                  onClick={() => handleDeleteClick(item)}
                  title={`${item.hospital_name} ${item.department_name} のレポートを削除`}
                  aria-label={`${item.hospital_name} ${item.department_name} のレポートを削除`}
                >
                  <Trash2 size={14} />
                </button>
              ) : null}
            </td>
          </tr>
        );
      }),
    [displayedReports, formatDate, getBusynessMetaSummary, getMealSummary, getSummaryBadges, handleDeleteClick, isMobileView, isTabletView]
  );

  const renderPagination = useCallback(() => {
    if (totalPages <= 1) return null;

    const pages = [];
    const maxPageButtons = 5;
    const pageBuffer = Math.floor(maxPageButtons / 2);
    let startPage = Math.max(1, currentPage - pageBuffer);
    let endPage = Math.min(totalPages, currentPage + pageBuffer);

    if (currentPage - pageBuffer <= 0) {
      endPage = Math.min(totalPages, maxPageButtons);
    }
    if (currentPage + pageBuffer >= totalPages) {
      startPage = Math.max(1, totalPages - maxPageButtons + 1);
    }

    for (let page = startPage; page <= endPage; page += 1) {
      pages.push(page);
    }

    return (
      <nav aria-label="申し送り一覧のページネーション" className="fg-pagination">
        <button type="button" className="fg-page-button fg-page-prev" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>
          前へ
        </button>

        {startPage > 1 && (
          <>
            <button type="button" className={`fg-page-button ${currentPage === 1 ? 'active' : ''}`} onClick={() => handlePageChange(1)}>
              1
            </button>
            {startPage > 2 && <span className="fg-page-ellipsis">...</span>}
          </>
        )}

        {pages.map((page) => (
          <button
            type="button"
            key={page}
            className={`fg-page-button ${page === currentPage ? 'active' : ''}`}
            onClick={() => handlePageChange(page)}
            aria-current={page === currentPage ? 'page' : undefined}
          >
            {page}
          </button>
        ))}

        {endPage < totalPages && (
          <>
            {endPage < totalPages - 1 && <span className="fg-page-ellipsis">...</span>}
            <button
              type="button"
              className={`fg-page-button ${currentPage === totalPages ? 'active' : ''}`}
              onClick={() => handlePageChange(totalPages)}
            >
              {totalPages}
            </button>
          </>
        )}

        <button type="button" className="fg-page-button fg-page-next" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}>
          次へ
        </button>
      </nav>
    );
  }, [currentPage, handlePageChange, totalPages]);

  const renderMobileCards = useCallback(
    () =>
      displayedReports.map((item, index) => {
        const cardId = item.latest_report_id ?? `report-${index}`;
        const isExpanded = expandedCardId === cardId;
        const summaryBadges = getSummaryBadges(item);
        const mealSummary = getMealSummary(item);
        const busynessMetaSummary = getBusynessMetaSummary(item);

        return (
          <div
            key={cardId}
            className={`fg-mobile-card ${index % 2 === 0 ? 'fg-card-even' : 'fg-card-odd'} ${isExpanded ? 'expanded' : ''}`}
            onClick={() => setExpandedCardId((prev) => (prev === cardId ? null : cardId))}
            tabIndex={0}
            role="button"
            aria-expanded={isExpanded}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                setExpandedCardId((prev) => (prev === cardId ? null : cardId));
                event.preventDefault();
              }
            }}
          >
            <div className="fg-card-header">
              <div className="fg-card-title">
                <span className="fg-card-hospital">{item.hospital_name || '病院名不明'}</span>
                <span className="fg-card-dept">{item.department_name || '科名不明'}</span>
                <span className="fg-card-term">ターム: {item.term ?? '-'}</span>
              </div>
              <div className="fg-card-expand-icon" aria-hidden="true">
                {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
              </div>
            </div>

            <div className="fg-card-content">
              <div className="fg-mobile-summary-block">
                <div className="fg-summary-badges">
                  {summaryBadges.length > 0 ? (
                    summaryBadges.map((badge) => (
                      <span key={badge} className="fg-summary-badge">
                        {badge}
                      </span>
                    ))
                  ) : (
                    <span className="fg-summary-empty">評価未設定</span>
                  )}
                </div>
                {busynessMetaSummary && <div className="fg-summary-subtext">{busynessMetaSummary}</div>}
                {mealSummary && <div className="fg-summary-subtext">{mealSummary}</div>}
              </div>

              <div className="fg-card-info-row">
                <span className="fg-card-label">更新日時</span>
                <span className="fg-card-value">{formatDate(item.last_updated_at)}</span>
              </div>
              <div className="fg-card-info-row">
                <span className="fg-card-label">更新ユーザー</span>
                <span className="fg-card-value">{item.last_updated_user || '-'}</span>
              </div>
              <div className="fg-card-info-row">
                <span className="fg-card-label">コホート</span>
                <span className="fg-card-value">{item.cohort_name || '-'}</span>
              </div>

              <div className="fg-card-actions">
                {item.latest_report_id ? (
                  <>
                    <Link
                      to={`/hospital-reports/${item.latest_report_id}`}
                      className="fg-card-link"
                      onClick={(event) => event.stopPropagation()}
                    >
                      詳細を見る
                      <ChevronRight size={16} className="ml-1" />
                    </Link>
                    <button
                      type="button"
                      className="fg-card-delete-button"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteClick(item);
                      }}
                    >
                      <Trash2 size={16} className="mr-1" />
                      削除
                    </button>
                  </>
                ) : (
                  <span className="fg-card-no-link">詳細はありません</span>
                )}
              </div>
            </div>
          </div>
        );
      }),
    [displayedReports, expandedCardId, formatDate, getBusynessMetaSummary, getMealSummary, getSummaryBadges, handleDeleteClick]
  );

  useEffect(() => {
    const savedVisibility = localStorage.getItem('hospitalReportFilterVisible');
    if (window.innerWidth > 768) {
      setIsFilterVisible(true);
      return;
    }
    if (savedVisibility !== null) {
      setIsFilterVisible(savedVisibility === 'true');
      return;
    }
    setIsFilterVisible(false);
  }, []);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const userInfo = await getUserInfo();
        setCurrentUser(userInfo);
      } catch (userError) {
        console.error('ユーザー情報の取得に失敗しました:', userError);
      }
    };
    fetchUser();
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [institutions, cohorts] = await Promise.all([getInstitutions(), getCohorts()]);
        setHospitalList(institutions);
        setCohortList(cohorts);
      } catch (initialError) {
        console.error('初期データの取得に失敗しました:', initialError);
        setError('病院・コホート情報の取得に失敗しました。');
      }
    };
    fetchInitialData();
  }, []);

  useEffect(() => {
    const fetchDepartments = async () => {
      if (!selectedHospital) {
        setDepartmentList([]);
        setSelectedDepartment('');
        return;
      }

      try {
        const capacities = await getDepartmentTermCapacities();
        const seen = new Set();
        const nextDepartments = [];

        capacities
          .filter((capacity) => capacity.institution_id === Number(selectedHospital))
          .forEach((capacity) => {
            if (!capacity.department_id || !capacity.department_name || seen.has(capacity.department_id)) {
              return;
            }
            seen.add(capacity.department_id);
            nextDepartments.push({ id: capacity.department_id, name: capacity.department_name });
          });

        setDepartmentList(nextDepartments);
      } catch (departmentError) {
        console.error('科一覧の取得に失敗しました:', departmentError);
        setDepartmentList([]);
        setError('科一覧の取得に失敗しました。');
      }
    };

    fetchDepartments();
  }, [selectedHospital]);

  useEffect(() => {
    const fetchReports = async () => {
      setIsLoading(true);
      setError('');
      setHasMoreItems(true);

      try {
        const params = {
          sort_by: sortKey,
          sort_order: sortOrder,
        };

        if (selectedHospital) params.hospital_id = selectedHospital;
        if (selectedDepartment) params.department_id = selectedDepartment;
        if (selectedTerm) params.term = selectedTerm;
        if (selectedCohort) params.cohort_id = selectedCohort;
        if (selectedTypicalStartTime) {
          params.typical_start_time = selectedTypicalStartTime;
          params.typical_start_time_operator = selectedTypicalStartTimeOperator;
        }
        if (selectedTypicalEndTime) {
          params.typical_end_time = selectedTypicalEndTime;
          params.typical_end_time_operator = selectedTypicalEndTimeOperator;
        }
        if (selectedLateDaysPerWeek !== '') {
          params.late_days_per_week = selectedLateDaysPerWeek;
          params.late_days_per_week_operator = selectedLateDaysPerWeekOperator;
        }
        if (selectedFreeStudyTime) params.free_study_time = selectedFreeStudyTime;
        if (selectedHasNightDuty !== '') params.has_night_duty = selectedHasNightDuty;
        if (selectedBusyness) params.busyness = selectedBusyness;
        if (selectedHospitalTourOption) params.hospital_tour_option = selectedHospitalTourOption;
        if (selectedCarRequirement) params.car_requirement = selectedCarRequirement;
        if (selectedAssignmentBurden) params.assignment_burden = selectedAssignmentBurden;
        if (selectedSelfStudyAvailability) params.self_study_availability = selectedSelfStudyAvailability;
        if (selectedHandsOnOpportunity) params.hands_on_opportunity = selectedHandsOnOpportunity;
        if (selectedLodgingConvenience) params.lodging_convenience = selectedLodgingConvenience;
        if (selectedTeachingQuality) params.teaching_quality = selectedTeachingQuality;
        if (selectedQuestionApproachability) params.question_approachability = selectedQuestionApproachability;
        if (selectedDepartmentAtmosphere) params.department_atmosphere = selectedDepartmentAtmosphere;
        if (selectedDoctorPersonality) params.doctor_personality = selectedDoctorPersonality;
        if (selectedBreakfast) params.breakfast = selectedBreakfast;
        if (selectedLunch) params.lunch = selectedLunch;
        if (selectedDinner) params.dinner = selectedDinner;

        const data = await getHospitalReportList(params);
        setReportList(data || []);
      } catch (reportError) {
        console.error('申し送り一覧の取得に失敗しました:', reportError);
        setError('申し送り一覧の取得に失敗しました。');
        setReportList([]);
        setFilteredReportList([]);
        setDisplayedReports([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchReports();
  }, [
    selectedAssignmentBurden,
    selectedBreakfast,
    selectedBusyness,
    selectedCarRequirement,
    selectedCohort,
    selectedFreeStudyTime,
    selectedDepartment,
    selectedDepartmentAtmosphere,
    selectedDinner,
    selectedDoctorPersonality,
    selectedHandsOnOpportunity,
    selectedHospital,
    selectedHospitalTourOption,
    selectedHasNightDuty,
    selectedLateDaysPerWeekOperator,
    selectedLunch,
    selectedLateDaysPerWeek,
    selectedLodgingConvenience,
    selectedQuestionApproachability,
    selectedSelfStudyAvailability,
    selectedTeachingQuality,
    selectedTerm,
    selectedTypicalEndTimeOperator,
    selectedTypicalEndTime,
    selectedTypicalStartTimeOperator,
    selectedTypicalStartTime,
    sortKey,
    sortOrder,
  ]);

  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      const keyword = searchKeyword.trim().toLowerCase();
      const nextFiltered =
        keyword === ''
          ? [...reportList]
          : reportList.filter((item) => {
              const targets = [item.hospital_name, item.department_name, item.last_updated_user, item.cohort_name]
                .filter(Boolean)
                .map((value) => value.toLowerCase());
              return targets.some((value) => value.includes(keyword));
            });

      setFilteredReportList(nextFiltered);
      setCurrentPage(1);
      setTotalPages(Math.max(1, Math.ceil(nextFiltered.length / ITEMS_PER_PAGE)));
      setExpandedCardId(null);

      if (isMobileView) {
        setDisplayedReports(nextFiltered.slice(0, ITEMS_PER_PAGE));
        setHasMoreItems(nextFiltered.length > ITEMS_PER_PAGE);
      } else {
        updateDisplayedData(1, nextFiltered);
        setHasMoreItems(false);
      }
    }, 250);

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [isMobileView, reportList, searchKeyword, updateDisplayedData]);

  useEffect(() => {
    if (isMobileView) {
      setDisplayedReports(filteredReportList.slice(0, currentPage * ITEMS_PER_PAGE));
      setHasMoreItems(currentPage * ITEMS_PER_PAGE < filteredReportList.length);
      return;
    }

    updateDisplayedData(currentPage, filteredReportList);
  }, [currentPage, filteredReportList, isMobileView, updateDisplayedData]);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const handleResize = () => {
      const nextWidth = window.innerWidth;
      setWindowWidth(nextWidth);

      if (nextWidth > 768) {
        setIsFilterVisible(true);
        return;
      }

      const savedVisibility = localStorage.getItem('hospitalReportFilterVisible');
      if (savedVisibility !== null) {
        setIsFilterVisible(savedVisibility === 'true');
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!isMobileView || !hasMoreItems || isLoadingMore) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          loadMoreItems();
        }
      },
      { threshold: 0.1 }
    );

    observerRef.current = observer;

    if (loadingMoreRef.current) {
      observer.observe(loadingMoreRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, [hasMoreItems, isLoadingMore, isMobileView, loadMoreItems]);

  const activeFilterCount = countActiveFilters([
    selectedHospital,
    selectedDepartment,
    selectedTerm,
    selectedCohort,
    selectedTypicalStartTimeOperator,
    selectedTypicalStartTime,
    selectedTypicalEndTimeOperator,
    selectedTypicalEndTime,
    selectedLateDaysPerWeekOperator,
    selectedLateDaysPerWeek,
    selectedFreeStudyTime,
    selectedHasNightDuty,
    selectedBusyness,
    selectedHospitalTourOption,
    selectedCarRequirement,
    selectedAssignmentBurden,
    selectedSelfStudyAvailability,
    selectedHandsOnOpportunity,
    selectedLodgingConvenience,
    selectedTeachingQuality,
    selectedQuestionApproachability,
    selectedDepartmentAtmosphere,
    selectedDoctorPersonality,
    selectedBreakfast,
    selectedLunch,
    selectedDinner,
    searchKeyword.trim(),
  ]);

  if (isLoading && reportList.length === 0) {
    return <LoadingSpinner text="申し送り一覧を読み込んでいます..." />;
  }

  return (
    <div className="fg-container hospital-report-list-container" ref={topRef} tabIndex={-1}>
      <OnboardingTour
        tourKey="hospital-report-list-tour"
        steps={TOUR_STEPS}
        autoStart={showTour}
        onComplete={() => setShowTour(false)}
      />

      <div className="fg-page-header" data-tour="report-list-header">
        <h1 className="fg-title">
          <FileText className="icon-blue" size={32} />
          申し送り一覧
        </h1>
        <p className="fg-description">病院・科ごとの申し送り情報を一覧で確認できます。</p>
      </div>

      <div className="fg-header-actions">
        <Link to="/hospital-report" className="fg-create-button btn btn-primary" data-tour="report-list-create-button">
          <FileText size={18} className="mr-2" />
          申し送りを作成する
        </Link>
        <a href={NOTEBOOK_LM_URL} className="fg-notebook-button btn" target="_blank" rel="noreferrer">
          <img src="/notebooklm-icon.svg" alt="" className="notebooklm-icon" />
          AIで内容を調べる
        </a>
        <button type="button" className="fg-guide-button btn btn-secondary" onClick={() => setShowTour(true)} title="ガイドを表示">
          <HelpCircle size={18} />
          ガイド
        </button>
      </div>

      <section
        className="fg-filter-section dashboard-card full-width"
        aria-labelledby="filter-title"
        data-tour="report-list-filter"
      >
        <div className="card-header">
          <h2
            id="filter-title"
            className={`card-title fg-filter-title ${isFilterVisible ? 'active' : ''}`}
            onClick={() => {
              if (!isMobileView) return;
              const nextVisible = !isFilterVisible;
              setIsFilterVisible(nextVisible);
              localStorage.setItem('hospitalReportFilterVisible', nextVisible.toString());
            }}
            role={isMobileView ? 'button' : undefined}
            tabIndex={isMobileView ? 0 : -1}
            aria-expanded={isMobileView ? isFilterVisible : undefined}
            onKeyDown={
              isMobileView
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      const nextVisible = !isFilterVisible;
                      setIsFilterVisible(nextVisible);
                      localStorage.setItem('hospitalReportFilterVisible', nextVisible.toString());
                      event.preventDefault();
                    }
                  }
                : undefined
            }
          >
            <Filter size={20} className="icon-blue" />
            フィルター
            {isMobileView && (
              <span className="fg-filter-toggle-icon" aria-hidden="true">
                {isFilterVisible ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </span>
            )}
          </h2>
        </div>

        {isFilterVisible && (
          <div id="filter-content-area" className="fg-filter-content">
            <div className="fg-search-container">
              <div className="fg-search-input-wrapper">
                <Search className="fg-search-icon" size={18} aria-hidden="true" />
                <input
                  type="search"
                  placeholder="病院名、科名、更新ユーザーで検索"
                  className="fg-search-input"
                  value={searchKeyword}
                  onChange={(event) => setSearchKeyword(event.target.value)}
                  aria-label="キーワード検索"
                />
                {searchKeyword && (
                  <button type="button" className="fg-search-clear" onClick={() => setSearchKeyword('')} aria-label="検索キーワードをクリア">
                    <X size={16} />
                  </button>
                )}
              </div>
            </div>

            <div className="fg-filter-toolbar">
              <span className="fg-filter-count">適用中の条件: {activeFilterCount}</span>
              <button type="button" className="fg-filter-reset-button" onClick={clearAllFilters}>
                すべてクリア
              </button>
            </div>

            <div className="fg-filter-grid fg-filter-grid-modern">
              <div className="fg-filter-item">
                <label htmlFor="hospital-filter-modern">病院</label>
                <select id="hospital-filter-modern" className="fg-filter-select" value={selectedHospital} onChange={(event) => setSelectedHospital(event.target.value)}>
                  <option value="">すべての病院</option>
                  {hospitalList.map((institution) => (
                    <option key={institution.id} value={institution.id}>
                      {institution.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="department-filter-modern">科</label>
                <select
                  id="department-filter-modern"
                  className="fg-filter-select"
                  value={selectedDepartment}
                  onChange={(event) => setSelectedDepartment(event.target.value)}
                  disabled={!selectedHospital || departmentList.length === 0}
                >
                  <option value="">すべての科</option>
                  {departmentList.map((department) => (
                    <option key={department.id} value={department.id}>
                      {department.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="term-filter-modern">ターム</label>
                <select id="term-filter-modern" className="fg-filter-select" value={selectedTerm} onChange={(event) => setSelectedTerm(event.target.value)}>
                  <option value="">すべてのターム</option>
                  {TERM_OPTIONS.map((term) => (
                    <option key={term} value={term}>
                      {term}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="cohort-filter-modern">コホート</label>
                <select id="cohort-filter-modern" className="fg-filter-select" value={selectedCohort} onChange={(event) => setSelectedCohort(event.target.value)}>
                  <option value="">すべてのコホート</option>
                  {cohortList.map((cohort) => (
                    <option key={cohort.id} value={cohort.id}>
                      {cohort.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="start-time-filter-modern">始業時間</label>
                <div className="fg-filter-inline">
                  <select
                    id="start-time-operator-modern"
                    className="fg-filter-select fg-filter-operator"
                    value={selectedTypicalStartTimeOperator}
                    onChange={(event) => setSelectedTypicalStartTimeOperator(event.target.value)}
                  >
                    <option value="gte">以降</option>
                    <option value="lte">以前</option>
                  </select>
                  <input
                    id="start-time-filter-modern"
                    type="time"
                    className="fg-filter-select"
                    value={selectedTypicalStartTime}
                    onChange={(event) => setSelectedTypicalStartTime(event.target.value)}
                  />
                </div>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="end-time-filter-modern">終業時間</label>
                <div className="fg-filter-inline">
                  <select
                    id="end-time-operator-modern"
                    className="fg-filter-select fg-filter-operator"
                    value={selectedTypicalEndTimeOperator}
                    onChange={(event) => setSelectedTypicalEndTimeOperator(event.target.value)}
                  >
                    <option value="lte">以前</option>
                    <option value="gte">以降</option>
                  </select>
                  <input
                    id="end-time-filter-modern"
                    type="time"
                    className="fg-filter-select"
                    value={selectedTypicalEndTime}
                    onChange={(event) => setSelectedTypicalEndTime(event.target.value)}
                  />
                </div>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="late-days-filter-modern">17時以降の日数</label>
                <div className="fg-filter-inline">
                  <select
                    id="late-days-operator-modern"
                    className="fg-filter-select fg-filter-operator"
                    value={selectedLateDaysPerWeekOperator}
                    onChange={(event) => setSelectedLateDaysPerWeekOperator(event.target.value)}
                  >
                    <option value="gte">以上</option>
                    <option value="lte">以下</option>
                  </select>
                  <select
                    id="late-days-filter-modern"
                    className="fg-filter-select"
                    value={selectedLateDaysPerWeek}
                    onChange={(event) => setSelectedLateDaysPerWeek(event.target.value)}
                  >
                    <option value="">指定なし</option>
                    {Array.from({ length: 8 }, (_, value) => (
                      <option key={value} value={value}>
                        週{value}日
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="free-study-time-filter-modern">自由時間</label>
                <select
                  id="free-study-time-filter-modern"
                  className="fg-filter-select"
                  value={selectedFreeStudyTime}
                  onChange={(event) => setSelectedFreeStudyTime(event.target.value)}
                >
                  <option value="">指定なし</option>
                  {Object.entries(FREE_STUDY_TIME_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="night-duty-filter-modern">当直・夜遅い日</label>
                <select
                  id="night-duty-filter-modern"
                  className="fg-filter-select"
                  value={selectedHasNightDuty}
                  onChange={(event) => setSelectedHasNightDuty(event.target.value)}
                >
                  <option value="">指定なし</option>
                  <option value="true">あり</option>
                  <option value="false">なし</option>
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="busyness-filter-modern">忙しさ</label>
                <select id="busyness-filter-modern" className="fg-filter-select" value={selectedBusyness} onChange={(event) => setSelectedBusyness(event.target.value)}>
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {value} ({BUSYNESS_LABELS[value]})
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="tour-filter-modern">病院見学</label>
                <select
                  id="tour-filter-modern"
                  className="fg-filter-select"
                  value={selectedHospitalTourOption}
                  onChange={(event) => setSelectedHospitalTourOption(event.target.value)}
                >
                  <option value="">指定なし</option>
                  <option value="possible">見学可</option>
                  <option value="impossible">見学不可</option>
                  <option value="unknown">見学不明</option>
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="car-filter-modern">車の必要性</label>
                <select id="car-filter-modern" className="fg-filter-select" value={selectedCarRequirement} onChange={(event) => setSelectedCarRequirement(event.target.value)}>
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="assignment-burden-filter">課題・発表の負担度</label>
                <select id="assignment-burden-filter" className="fg-filter-select" value={selectedAssignmentBurden} onChange={(event) => setSelectedAssignmentBurden(event.target.value)}>
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="self-study-filter">自習時間の確保しやすさ</label>
                <select id="self-study-filter" className="fg-filter-select" value={selectedSelfStudyAvailability} onChange={(event) => setSelectedSelfStudyAvailability(event.target.value)}>
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="hands-on-filter">手技・実践機会</label>
                <select id="hands-on-filter" className="fg-filter-select" value={selectedHandsOnOpportunity} onChange={(event) => setSelectedHandsOnOpportunity(event.target.value)}>
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="lodging-convenience-filter">宿舎の利便性・快適さ</label>
                <select id="lodging-convenience-filter" className="fg-filter-select" value={selectedLodgingConvenience} onChange={(event) => setSelectedLodgingConvenience(event.target.value)}>
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="teaching-quality-filter">教育の質</label>
                <select id="teaching-quality-filter" className="fg-filter-select" value={selectedTeachingQuality} onChange={(event) => setSelectedTeachingQuality(event.target.value)}>
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="question-approachability-filter">質問のしやすさ</label>
                <select
                  id="question-approachability-filter"
                  className="fg-filter-select"
                  value={selectedQuestionApproachability}
                  onChange={(event) => setSelectedQuestionApproachability(event.target.value)}
                >
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="department-atmosphere-filter">医局の雰囲気</label>
                <select
                  id="department-atmosphere-filter"
                  className="fg-filter-select"
                  value={selectedDepartmentAtmosphere}
                  onChange={(event) => setSelectedDepartmentAtmosphere(event.target.value)}
                >
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="doctor-personality-filter">医師の人柄</label>
                <select id="doctor-personality-filter" className="fg-filter-select" value={selectedDoctorPersonality} onChange={(event) => setSelectedDoctorPersonality(event.target.value)}>
                  <option value="">指定なし</option>
                  {RATING_OPTIONS.map((value) => (
                    <option key={value} value={value}>
                      {formatRating(value)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="breakfast-filter-modern">朝食</label>
                <select id="breakfast-filter-modern" className="fg-filter-select" value={selectedBreakfast} onChange={(event) => setSelectedBreakfast(event.target.value)}>
                  <option value="">指定なし</option>
                  <option value="none">なし</option>
                  <option value="free">無料</option>
                  <option value="paid">有料</option>
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="lunch-filter-modern">昼食</label>
                <select id="lunch-filter-modern" className="fg-filter-select" value={selectedLunch} onChange={(event) => setSelectedLunch(event.target.value)}>
                  <option value="">指定なし</option>
                  <option value="none">なし</option>
                  <option value="free">無料</option>
                  <option value="paid">有料</option>
                </select>
              </div>

              <div className="fg-filter-item">
                <label htmlFor="dinner-filter-modern">夕食</label>
                <select id="dinner-filter-modern" className="fg-filter-select" value={selectedDinner} onChange={(event) => setSelectedDinner(event.target.value)}>
                  <option value="">指定なし</option>
                  <option value="none">なし</option>
                  <option value="free">無料</option>
                  <option value="paid">有料</option>
                </select>
              </div>
            </div>
          </div>
        )}
      </section>

      {isLoading && (
        <div className="fg-loading-container" aria-label="読み込み中">
          <LoadingSpinner text="データを読み込んでいます..." fullScreen={false} />
        </div>
      )}

      {error && !isLoading && (
        <div className="fg-error-container" role="alert">
          <AlertCircle size={24} className="mr-2" />
          <p className="fg-error-text">{error}</p>
        </div>
      )}

      {!isLoading && !error && (
        <section className="fg-list-section dashboard-card full-width" ref={resultsRef} aria-live="polite" data-tour="report-list-results">
          {renderListSummary()}

          {filteredReportList.length > 0 ? (
            isMobileView ? (
              <div className="fg-mobile-cards-container">
                {renderMobileCards()}
                {hasMoreItems && (
                  <div ref={loadingMoreRef} className={`fg-loading-more ${isLoadingMore ? 'loading' : ''}`} aria-live="polite">
                    {isLoadingMore && (
                      <>
                        <div className="fg-loading-more-spinner" aria-hidden="true" />
                        <p>さらに読み込み中...</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="fg-table-wrapper">
                  <table className="fg-table" aria-label="申し送り一覧テーブル">
                    <thead>{renderTableHeaders()}</thead>
                    <tbody>{renderTableRows()}</tbody>
                  </table>
                </div>
                {renderPagination()}
              </>
            )
          ) : (
            <div className="fg-no-data-container">
              <p className="fg-no-data">条件に一致するデータがありません</p>
              {activeFilterCount > 0 && (
                <p className="fg-no-data-hint">フィルター条件または検索キーワードを見直してください。</p>
              )}
            </div>
          )}
        </section>
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDelete}
        reportData={reportToDelete}
        formData={deleteFormData}
        onFormChange={handleDeleteFormChange}
        isDeleting={isDeleting}
        error={deleteError}
        currentUser={currentUser}
        step={deleteStep}
        onBack={() => setDeleteStep(1)}
        deleteToken={deleteToken}
        deleteTokenInput={deleteTokenInput}
        onTokenInputChange={(event) => setDeleteTokenInput(event.target.value)}
      />

      <button
        type="button"
        className={`fg-scroll-top-button ${showScrollTop ? 'visible' : ''}`}
        onClick={scrollToTop}
        aria-label="ページ上部へ戻る"
        title="ページ上部へ戻る"
      >
        <ArrowUp size={20} color="white" />
      </button>
    </div>
  );
}

export default HospitalReportListPage;
